import React, { useState, useEffect } from "react";
import { liveUrl, token } from "./url";
import Cookies from "js-cookie";

const LoginComponent = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState("");
  const [moblie, setMoblie] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [validationErrors, setValidationErrors] = useState({
    username: "",
    moblie: "",
    otp: "",
  });
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [userId, setUserId] = useState("");
  const [userToken, setUserToken] = useState("");
  const [otpTimer, setOtpTimer] = useState(120); // 2 minutes (120 seconds)
  const [canResend, setCanResend] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);

  // Validation for name and mobile
  const validateFields = () => {
    let errors = { username: "", moblie: "" };
    if (!username.trim()) errors.username = "Name is required";
    if (!moblie.trim()) errors.moblie = "Mobile number is required";
    else if (moblie.length !== 10) errors.moblie = "Enter 10-digit mobile number";
    setValidationErrors(errors);
    return !errors.username && !errors.moblie;
  };

  // Validation for OTP
  const validateOtp = () => {
    let errors = { otp: "" };
    if (!otp.trim()) errors.otp = "OTP is required";
    else if (otp.length !== 6) errors.otp = "Enter 6-digit OTP";
    setValidationErrors((prev) => ({ ...prev, otp: errors.otp }));
    return !errors.otp;
  };

  // OTP Timer Logic
  useEffect(() => {
    let interval;
    if (showOtpScreen && otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showOtpScreen, otpTimer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "moblie") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);
      setMoblie(numericValue);
    } else if (name === "otp") {
      const numericValue = value.replace(/\D/g, "").slice(0, 6);
      setOtp(numericValue);
    } else {
      setUsername(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await fetch(`${liveUrl}api/User/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: username.trim(),
          mobile: moblie.trim(),
        }),
      });
      const data = await response.json();
      if (response.ok) {
        if (data.status === "done" && data.otp) {
          // New user - show OTP screen
          setUserId(data.id);
          setUserToken(data.token);
          setShowOtpScreen(true);
          setOtpTimer(120);
          setCanResend(false);
          setIsExistingUser(false);
          setSuccess(data.message || "OTP sent successfully!");
        } else if (data.token || data.message?.includes("already exists")) {
          // Existing user - don't show OTP screen, login directly
          setIsExistingUser(true);
          Cookies.set("token", data.token || "existing_user_token_placeholder", { expires: 7 });
          Cookies.set("userName", username, { expires: 7 });
          Cookies.set("moblie", moblie, { expires: 7 });
          Cookies.set("isLoggedIn", "true", { expires: 7 });
          Cookies.set("userId", data.userid?.toString() || data.id?.toString() || "", { expires: 7 });

          setSuccess(data.message || "Login successful!");
          setTimeout(() => {
            onLoginSuccess();
          }, 500);
        } else {
          setError(data.message || "Failed to authenticate. Please try again.");
        }
      } else {
        setError(data.message || "Failed to authenticate. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async (e) => {
    e.preventDefault();
    if (!validateOtp()) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const verifyResponse = await fetch(`${liveUrl}verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mobile: moblie.trim(),
          otp: otp.trim(),
        }),
      });
      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        throw new Error(errorData.message || "OTP verification failed");
      }
      const verifyData = await verifyResponse.json();
      if (verifyData.status === true || verifyData.message?.includes("verified")) {
        Cookies.set("token", userToken, { expires: 7 });
        Cookies.set("userName", username, { expires: 7 });
        Cookies.set("moblie", moblie, { expires: 7 });
        Cookies.set("isLoggedIn", "true", { expires: 7 });
        Cookies.set("userId", userId, { expires: 7 });
        setSuccess("OTP verified successfully!");
        setTimeout(() => {
          onLoginSuccess();
        }, 500);
      } else {
        setError(verifyData.message || "Invalid OTP. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Failed to verify OTP. Please try again.");
      console.error("OTP verification error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const response = await fetch(`${liveUrl}api/User/resendOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          mobile: moblie.trim(),
        }),
      });
      const data = await response.json();
      if (response.ok && data.status === "done") {
        setSuccess(data.message || "OTP resent successfully!");
        setOtpTimer(120); // Reset timer to 2 minutes
        setCanResend(false);
      } else {
        setError(data.message || "Failed to resend OTP. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again.");
      console.error("Resend OTP error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Only show OTP screen for new users
  if (showOtpScreen && !isExistingUser) {
    return (
      <div className="bg-gray-50 rounded-lg p-4">

        <div>
          <div className="mb-4">
            <label className="text-lg font-semibold block text-gray-800">
              Enter OTP*
            </label>
            <input
              type="tel"
              name="otp"
              value={otp}
              onChange={handleInputChange}
              placeholder="Enter 6-digit OTP"
              maxLength="6"
              className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
                validationErrors.otp ? "border-red-500" : "border-gray-300"
              }`}
            />
            {validationErrors.otp && (
              <p className="text-red-500 text-sm mt-2">
                {validationErrors.otp}
              </p>
            )}
          </div>
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          {success && <p className="text-green-600 text-sm mb-4">{success}</p>}
          <button
            onClick={handleOtpVerification}
            className={`w-full bg-red-600 text-white py-2 rounded-lg transition-colors mb-3 ${
              loading ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={loading}
          >
            {loading ? "Verifying..." : "Verify OTP"}
          </button>
          <div className="text-center mt-3 text-sm">
            {otpTimer > 0 ? (
              <span className="text-gray-600">
                Resend OTP in {otpTimer}s
              </span>
            ) : (
              <button
                onClick={handleResendOtp}
                className="text-blue-600 underline cursor-pointer font-medium"
                disabled={loading}
              >
                Resend OTP
              </button>
            )}
          </div>
          <button
            onClick={() => {
              setShowOtpScreen(false);
              setOtp("");
              setError("");
              setSuccess("");
              setIsExistingUser(false);
            }}
            className="w-full text-red-600 underline text-sm mt-2"
          >
            Change Mobile Number
          </button>
        </div>
      </div>
    );
  }

  // Show the login form for all users
  return (
    <div className="bg-gray-50 rounded-lg p-4">
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="text-lg font-semibold block text-gray-800">
            Your Name*
          </label>
          <input
            type="text"
            name="username"
            value={username}
            onChange={handleInputChange}
            placeholder="Enter your name"
            className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
              validationErrors.username ? "border-red-500" : "border-gray-300"
            }`}
          />
          {validationErrors.username && (
            <p className="text-red-500 text-sm mt-2">
              {validationErrors.username}
            </p>
          )}
        </div>
        <div className="mb-4">
          <label className="text-lg font-semibold block text-gray-800">
            Mobile Number*
          </label>
          <input
            type="tel"
            name="moblie"
            value={moblie}
            onChange={handleInputChange}
            placeholder="Enter your mobile number"
            maxLength="10"
            className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 ${
              validationErrors.moblie ? "border-red-500" : "border-gray-300"
            }`}
          />
          {validationErrors.moblie && (
            <p className="text-red-500 text-sm mt-2">
              {validationErrors.moblie}
            </p>
          )}
        </div>
        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
        {success && <p className="text-green-600 text-sm mb-4">{success}</p>}
        <button
          type="submit"
          className={`w-full bg-red-600 text-white py-2 rounded-lg transition-colors ${
            loading ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={loading}
        >
          {loading ? "Processing..." : "Continue"}
        </button>
      </form>
    </div>
  );
};

export default LoginComponent;
